import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './classic/Navbar';
import { NavbarActionsProvider } from '../../context/NavbarActionsContext';
import TopLoadingBar from '../TopLoadingBar';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { useStreamChat } from '../../context/StreamChatContext';
import StreamChatBox from '../Chat/StreamChatBox';
import NotificationManager from '../Notifications/NotificationManager';
import MfaEnforcementGuard from '../MfaEnforcementGuard';

/**
 * Classic desktop shell (the pre-2026-09 layout): hover-expand sidebar on
 * the left, floating icon tray top-right, grey canvas with grid + glow.
 * Selected per user in Settings → Appearance → Layout ("Classic"); the
 * alternative is InsetLayout. Kept byte-for-byte close to the original so the
 * two shells can be compared and the classic one retired later.
 */
const ClassicLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : true;
  });
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { userSettings } = useUserSettingsContext();
  const { initializeChat } = useStreamChat();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  const userId = useMemo(() => {
    if (userSettings) {
      if (userSettings.cognitoId) return userSettings.cognitoId;
      if (userSettings.cognitoUsername) return userSettings.cognitoUsername;
      if (userSettings.email) return userSettings.email;
    }
    try {
      const userPoolId = localStorage.getItem('amplify-signin-with-webui');
      if (userPoolId) {
        const userData = JSON.parse(localStorage.getItem(userPoolId));
        if (userData && userData.username) return userData.username;
      }
    } catch (_) { /* malformed storage */ }
    return null;
  }, [userSettings]);

  useEffect(() => {
    if (userId && !chatInitialized) {
      initializeChat(userId);
      setChatInitialized(true);
    }
  }, [userId, initializeChat, chatInitialized]);

  useEffect(() => {
    setIsLoading(true);
    const timeout = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  const toggleChat = useCallback(() => setIsChatOpen((prev) => !prev), []);

  const openChatModal = useCallback((channel = null) => {
    setIsChatOpen(true);
    if (channel) {
      sessionStorage.setItem('selectedChatChannel', JSON.stringify({ id: channel.id, timestamp: Date.now() }));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('selectChatChannel', { detail: { channel } }));
      }, 100);
    }
  }, []);

  useEffect(() => {
    window.openChatModal = openChatModal;
    const handleOpenChatModal = (event) => {
      const { channel, action } = event.detail || {};
      if (action === 'open') openChatModal(channel);
    };
    window.addEventListener('openChatModal', handleOpenChatModal);
    return () => {
      window.openChatModal = null;
      window.removeEventListener('openChatModal', handleOpenChatModal);
    };
  }, [openChatModal]);

  // pt-6: the navbar is a floating tray, so content starts near the top.
  const mainContentClasses = `
    pt-6 px-2 overflow-auto transition-all duration-500
    ${isSidebarCollapsed ? 'ml-24' : 'ml-72'}
    ${isChatOpen ? 'mr-[40%] lg:mr-[35%] xl:mr-[30%]' : 'mr-0'}
    relative
  `;

  const handleSidebarHoverChange = (isHovered) => {
    if (isHovered) setSidebarHovered(true);
    else setTimeout(() => setSidebarHovered(false), 100);
  };

  return (
    <NavbarActionsProvider>
      {/* `isolate`: the z-[-1] decoration layers paint above this canvas but below all content. */}
      <div className="min-h-screen relative isolate" style={{ background: 'var(--lodge-onyx-50)' }}>
        <div
          className="fixed inset-0 pointer-events-none lodge-canvas-grid"
          style={{
            backgroundImage: `
              linear-gradient(to right, var(--lodge-onyx-100) 1px, transparent 1px),
              linear-gradient(to bottom, var(--lodge-onyx-100) 1px, transparent 1px)
            `,
            backgroundSize: '32px 32px',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 100%), linear-gradient(to bottom, black 0%, black 90%, transparent 100%)',
            WebkitMaskComposite: 'destination-in',
            maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 100%), linear-gradient(to bottom, black 0%, black 90%, transparent 100%)',
            maskComposite: 'intersect',
            zIndex: -1,
          }}
        />
        <div className="fixed top-0 left-0 right-0 pointer-events-none lodge-canvas-glow" style={{ zIndex: -1, height: 340 }} />
        <MfaEnforcementGuard key={`mfa-guard-${location.pathname}`} />
        <TopLoadingBar isLoading={isLoading} />

        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onHoverChange={handleSidebarHoverChange}
        />
        <Navbar isSidebarCollapsed={isSidebarCollapsed} sidebarHovered={sidebarHovered} toggleChat={toggleChat} />

        <main className={mainContentClasses}>
          <div className="h-full">
            <Outlet />
          </div>
        </main>

        {userId && (
          <StreamChatBox isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} userId={userId} isSidebarCollapsed={isSidebarCollapsed} />
        )}

        <NotificationManager
          onNotificationClick={(notification) => {
            if (notification.type === 'chat-message') openChatModal(notification.channel);
          }}
        />
      </div>
    </NavbarActionsProvider>
  );
};

export default ClassicLayout;
